// The weekly update from the Executive page, as the field sees it (Ryan, Oct 4 2026): a card
// at the top of the phone's home screen — bold blue since Oct 5 2026 (Pat), built like the red
// Directive card. Read it, "Got it" and it's gone. Nothing to do but read it.
import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch, API_BASE } from '../services/apiClient';

const fileUrl = fn => `${API_BASE.replace('/api', '')}/uploads/${fn}`;

const day = iso => { const d = new Date(iso); return isNaN(d) ? '' : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); };
const isImage = f => /^image\//.test(f.mime_type || '');

export default function FieldUpdateBanner({ onChange } = {}) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState('');
  const load = useCallback(() => apiFetch('/field-updates-mine').then(r => setRows(Array.isArray(r) ? r : [])).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 5 * 60 * 1000); return () => clearInterval(t); }, [load]);
  async function gotIt(id) {
    setBusy(id);
    try { await apiFetch(`/field-updates/${id}/read`, { method: 'POST', body: '{}' }); setRows(rs => rs.filter(r => r.id !== id)); setOpen(null); onChange && onChange(); }
    catch { /* stays up; they can try again */ }
    setBusy('');
  }
  if (!rows.length) return null;
  // The newest is big, any older ones stack smaller beneath it. A short one shows in full; a
  // long one (or one with photos / PDFs) opens on a tap.
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
      {rows.map((u, i) => {
        const big = i === 0;
        const files = Array.isArray(u.files) ? u.files : [];
        const long = String(u.body || '').length > 280 || files.length > 0;
        const isOpen = !long || open === u.id;
        return (
          <div key={u.id} style={{ background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', color: '#fff', borderRadius: 16, padding: big ? '18px 20px' : '13px 16px', boxShadow: '0 8px 24px rgba(37,99,235,0.32)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', opacity: 0.92 }}>
              <span style={{ fontSize: 15, lineHeight: 1 }}>📢</span>
              Weekly update · {day(u.created_at)}{u.from_name ? ` · ${u.from_name}` : ''}
            </div>
            <div style={{ fontSize: big ? 20 : 16, fontWeight: 800, lineHeight: 1.3, margin: '8px 0 2px' }}>{u.title}</div>
            {u.body && isOpen && <div style={{ fontSize: big ? 15 : 14, lineHeight: 1.5, whiteSpace: 'pre-wrap', marginTop: 6 }}>{u.body}</div>}
            {/* Photos show right here; a PDF opens on a tap. */}
            {isOpen && files.filter(isImage).map(f => (
              <a key={f.filename} href={fileUrl(f.filename)} target="_blank" rel="noreferrer" style={{ display: 'block', marginTop: 10 }}>
                <img src={fileUrl(f.filename)} alt={f.original_name} style={{ width: '100%', maxHeight: 320, objectFit: 'contain', borderRadius: 10, background: '#fff' }} />
              </a>
            ))}
            {isOpen && files.filter(f => !isImage(f)).map(f => (
              <a key={f.filename} href={fileUrl(f.filename)} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, padding: '10px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.45)', color: '#fff', fontWeight: 800, fontSize: 14, textDecoration: 'none' }}>
                📄 <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.original_name}</span> <span style={{ fontSize: 12.5 }}>Open ›</span>
              </a>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: big ? 14 : 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <button onClick={() => gotIt(u.id)} disabled={busy === u.id} style={{ background: '#fff', border: 'none', color: '#1d4ed8', borderRadius: 10, padding: big ? '11px 22px' : '8px 16px', fontWeight: 800, fontSize: big ? 15 : 13, cursor: 'pointer' }}>{busy === u.id ? '…' : '✓ Got it'}</button>
              {long && (
                <button onClick={() => setOpen(o => (o === u.id ? null : u.id))} style={{ background: 'rgba(255,255,255,0.2)', border: '1.5px solid rgba(255,255,255,0.5)', color: '#fff', borderRadius: 10, padding: big ? '10px 18px' : '7px 14px', fontWeight: 800, fontSize: big ? 14 : 12.5, cursor: 'pointer' }}>
                  {isOpen ? 'Less ▲' : `${files.length ? '📎 ' : ''}Read it ▼`}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
